import React, { useMemo } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import CommonDropdown from '../../../../components/common-dropdown/CommonDropdown';
import getMaxSonarDate from '../../../../utils/getMaxSonarDate';
import useMonthsOptions from '../../../../hooks/useMonthsOptions';
import useYearsOptions from '../../../../hooks/useYearsOptions';
import useProjectRangeTypeOptions from '../../../../hooks/useProjectRangeTypeOptions';
import getPossibleMonths from '../../../../utils/getPossibleMonths';
import { Input } from '../../../../components/common-inputs';

import scss from './SonarNameFormInner.module.scss';

function SonarNameFormInner() {
  const { t } = useTranslation('projects');

  const { change } = useForm();
  const { values } = useFormState();

  const maxDate = getMaxSonarDate();

  const monthsOptions = useMonthsOptions();
  const projectRangeTypeOptions = useProjectRangeTypeOptions();
  const yearOptions = useYearsOptions({ endYearProps: +maxDate.year });
  const isLoading = useSelector((state) => state.projects.isNameSectionLoading);

  const possibleMonths = useMemo(
    () => getPossibleMonths(+values.startingFromYear, monthsOptions, maxDate),
    [values.startingFromYear, monthsOptions, maxDate],
  );

  const handleChangeStartMonth = (value) => {
    change('startingFromMonth', value);
    // onBlur('startingFromMonth', value);
  };

  const handleChangeStartYear = (value) => {
    change('startingFromYear', value);

    const newPossibleMonth = getPossibleMonths(+value, monthsOptions, maxDate);

    if (!newPossibleMonth.map(({ value }) => +value).includes(+values.startingFromMonth)) {
      change('startingFromMonth', '');
    }
  };

  return (
    <>
      <Field name='brand'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('brand')}:`} maxWidth={360} isBold>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                value={input.value}
                onChange={input.onChange}
                name={input.name}
                size='sm'
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                disabled={isLoading}
                onBlur={input.onBlur}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='rangeType'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('rangeType')}:`} maxWidth={360} isBold>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                value={input.value}
                options={projectRangeTypeOptions}
                onSelect={input.onChange}
                name={input.name}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                disabled={isLoading}
                onBlur={input.onBlur}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('startingFrom')}:`} maxWidth={360} isBold>
        <div className={scss.startingFromDropdowns}>
          <Field name='startingFromMonth' autoComplete='starting-from-month'>
            {({ meta, input }) => (
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  value={input.value}
                  options={possibleMonths}
                  onSelect={handleChangeStartMonth}
                  name={input.name}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={isLoading}
                  onBlur={input.onBlur}
                />
              </WithError>
            )}
          </Field>

          <Field name='startingFromYear' autoComplete='starting-from-year'>
            {({ meta, input }) => (
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  value={input.value}
                  options={yearOptions}
                  onSelect={handleChangeStartYear}
                  name={input.name}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={isLoading}
                  onBlur={input.onBlur}
                />
              </WithError>
            )}
          </Field>
        </div>
      </WithLabel>
    </>
  );
}

export default SonarNameFormInner;
